/**
 * Care & Manual Section Manager
 * Combines collection tab switching and FAQ functionality
 */

class FAQManager {
    constructor(container) {
      this.container = container;
      this.tabButtons = container.querySelectorAll('[data-tab]');
      this.tabContents = container.querySelectorAll('[role="tabpanel"]');
      this.faqItems = container.querySelectorAll('[data-faq-item]');
      this.activeTab = this.tabButtons[0]?.dataset.tab || '';
      this.boundHandlers = new Map(); // Store bound handlers for cleanup
      this.faqTabsNav = container.querySelector('.swiper-tabs-nav'); // Mobile FAQ tabs navigation
      this.stickyTimeout = null; // Store timeout for cleanup
      this.faqScrollHandler = null; // Store scroll handler for cleanup
      this.faqResizeHandler = null; // Store resize handler for cleanup
      this.scrollTicking = false; // For scroll throttling
      this.faqNavPlaceholder = null; // Placeholder element for fixed positioning
      this.faqNavOriginalTop = 0; // Original top position
      this.faqNavIsFixed = false; // Track fixed state
      
      this.init();
    }
  
    init() {
      this.cleanup(); // Clean up any existing listeners
      this.initTabButtons();
      this.initFAQItems();
      this.makeFAQTabsSticky(); // Make FAQ tabs sticky on mobile
      this.switchTab(this.activeTab);
    }
  
    cleanup() {
      // Remove all existing event listeners
      this.boundHandlers.forEach((handler, element) => {
        element.removeEventListener('click', handler);
      });
      this.boundHandlers.clear();
    }
  
    initTabButtons() {
      this.tabButtons.forEach(button => {
        const handler = () => {
          const newTabId = button.dataset.tab;
          if (newTabId !== this.activeTab) {
            this.switchTab(newTabId);
          }
        };
        button.addEventListener('click', handler);
        this.boundHandlers.set(button, handler);
      });
    }
  
    initFAQItems() {
      this.faqItems.forEach(item => {
        const button = item.querySelector('button');
        if (button) {
          const handler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleFAQ(item);
          };
          button.addEventListener('click', handler);
          this.boundHandlers.set(button, handler);
        }
      });
    }
  
    makeFAQTabsSticky() {
      // Make FAQ category tabs fixed on mobile (more reliable than sticky)
      if (this.faqTabsNav && window.innerWidth < 768) {
        const navWrapper = this.faqTabsNav.closest('nav');
        if (navWrapper) {
          // Store original position info
          this.faqNavOriginalTop = navWrapper.offsetTop;
          this.faqNavIsFixed = false;
          
          // Find the section container to check boundaries
          const sectionContainer = navWrapper.closest('.shopify-section') || 
                                  navWrapper.closest('section') ||
                                  navWrapper.closest('[data-section-id]');
          
          // Create placeholder to maintain layout when nav becomes fixed
          this.faqNavPlaceholder = document.createElement('div');
          this.faqNavPlaceholder.style.display = 'none';
          this.faqNavPlaceholder.style.height = '0';
          navWrapper.parentNode.insertBefore(this.faqNavPlaceholder, navWrapper);
          
          // Add transition for smooth animation
          navWrapper.style.transition = 'all 0.3s ease';
          
          // Function to update fixed position
          const updateFixedPosition = () => {
            // Calculate header height
            const headerSelectors = [
              '.header-wrapper.header-wrapper--overlay.header-transparent',
              '.header-transparent',
              '.header-wrapper',
              'header',
              '.header',
              '[data-header]',
              'sticky-header'
            ];
            
            let header = null;
            for (const selector of headerSelectors) {
              header = document.querySelector(selector);
              if (header) break;
            }
            
            let headerHeight = 60; // Default fallback
            if (header) {
              const rect = header.getBoundingClientRect();
              headerHeight = rect.bottom;
            }
            
            // Get collection tabs height
            let collectionTabsHeight = 0;
            const allSwiperTabs = document.querySelectorAll('.swiper-tabs-nav');
            allSwiperTabs.forEach(tabs => {
              if (!tabs.closest('.data-faq-container')) {
                const container = tabs.closest('nav')?.parentElement;
                if (container) {
                  const computedStyle = window.getComputedStyle(container);
                  if (computedStyle.position === 'fixed') {
                    collectionTabsHeight = container.offsetHeight;
                  }
                }
              }
            });
            
            const totalTopOffset = headerHeight + collectionTabsHeight;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            // Check if we're still within section bounds
            let isWithinSection = true;
            if (sectionContainer) {
              const sectionRect = sectionContainer.getBoundingClientRect();
              const sectionBottom = sectionRect.bottom;
              const navHeight = navWrapper.offsetHeight || 0;
              
              // Buffer zone with strong hysteresis to prevent flickering
              // When fixed: unstick at 50px from bottom
              // When not fixed: only stick if there's 150px clearance (100px extra hysteresis)
              const baseBuffer = 50;
              const effectiveBuffer = this.faqNavIsFixed ? baseBuffer : baseBuffer + 100;
              
              // If section bottom is above the fixed position + buffer, we've scrolled past
              isWithinSection = sectionBottom > (totalTopOffset + navHeight + effectiveBuffer);
            }
            
            const shouldBeFixed = (scrollTop + totalTopOffset >= this.faqNavOriginalTop) && isWithinSection;
            
            if (shouldBeFixed && !this.faqNavIsFixed) {
              // Make it fixed
              this.faqNavIsFixed = true;
              const navHeight = navWrapper.offsetHeight;
              
              // Show placeholder
              this.faqNavPlaceholder.style.display = 'block';
              this.faqNavPlaceholder.style.height = `${navHeight}px`;
              
              // Make nav fixed
              navWrapper.style.position = 'fixed';
              navWrapper.style.top = `${totalTopOffset}px`;
              navWrapper.style.left = '1rem';
              navWrapper.style.right = '0';
              navWrapper.style.zIndex = '35';
              navWrapper.style.backgroundColor = '#f3f3f3';
              navWrapper.style.paddingTop = '5px';
              navWrapper.style.paddingBottom = '5px';
            } else if ((!shouldBeFixed || !isWithinSection) && this.faqNavIsFixed) {
              // Remove fixed
              this.faqNavIsFixed = false;
              
              // Hide placeholder
              this.faqNavPlaceholder.style.display = 'none';
              this.faqNavPlaceholder.style.height = '0';
              
              // Reset nav styles
              navWrapper.style.position = '';
              navWrapper.style.top = '';
              navWrapper.style.left = '';
              navWrapper.style.right = '';
              navWrapper.style.zIndex = '';
              navWrapper.style.backgroundColor = '';
              navWrapper.style.paddingTop = '';
              navWrapper.style.paddingBottom = '';
            } else if (this.faqNavIsFixed) {
              // Update fixed position (in case collection tabs state changed)
              navWrapper.style.top = `${totalTopOffset}px`;
            }
          };
          
          // Scroll handler with RAF throttling
          const scrollHandler = () => {
            if (!this.scrollTicking) {
              requestAnimationFrame(() => {
                updateFixedPosition();
                this.scrollTicking = false;
              });
              this.scrollTicking = true;
            }
          };
          
          // Initial check after delay
          this.stickyTimeout = setTimeout(updateFixedPosition, 200);
          
          // Add continuous scroll listener
          window.addEventListener('scroll', scrollHandler, { passive: true });
          
          // Add resize listener to remove fixed when switching to desktop
          const resizeHandler = () => {
            if (window.innerWidth >= 768) {
              // Switched to desktop - remove fixed positioning
              if (this.faqNavIsFixed) {
                this.faqNavIsFixed = false;
                
                // Hide placeholder
                if (this.faqNavPlaceholder) {
                  this.faqNavPlaceholder.style.display = 'none';
                  this.faqNavPlaceholder.style.height = '0';
                }
                
                // Reset nav styles
                navWrapper.style.position = '';
                navWrapper.style.top = '';
                navWrapper.style.left = '';
                navWrapper.style.right = '';
                navWrapper.style.zIndex = '';
                navWrapper.style.backgroundColor = '';
                navWrapper.style.paddingTop = '';
                navWrapper.style.paddingBottom = '';
              }
              
              // Remove scroll listener on desktop
              window.removeEventListener('scroll', scrollHandler);
              window.removeEventListener('resize', resizeHandler);
              this.faqScrollHandler = null;
              this.faqResizeHandler = null;
            }
          };
          
          window.addEventListener('resize', resizeHandler);
          
          // Store handlers for cleanup
          this.faqScrollHandler = scrollHandler;
          this.faqResizeHandler = resizeHandler;
        }
      }
    }
  
    calculateDynamicOffset() {
      let headerOffset = 0;
      
      // Find and calculate header offset
      const headerSelectors = [
        '.header-wrapper.header-wrapper--overlay.header-transparent',
        '.header-transparent',
        '.header-wrapper',
        'header',
        '.header',
        '[data-header]',
        'sticky-header'
      ];
      
      let header = null;
      for (const selector of headerSelectors) {
        header = document.querySelector(selector);
        if (header) break;
      }
      
      if (header) {
        const rect = header.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(header);
        
        if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') {
          // For absolute/fixed positioned headers, use the bottom edge position
          headerOffset = rect.bottom;
        } else {
          // For relative/static positioned headers, use height + margins
          const height = header.offsetHeight || 60;
          const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
          headerOffset = height + marginBottom;
        }
      } else {
        headerOffset = 109; // Fallback value
      }
      
      // Get horizontal tabs height
      const horizontalTabs = document.querySelector('.swiper-tabs-nav')?.closest('nav')?.parentElement;
      const horizontalTabsHeight = horizontalTabs ? horizontalTabs.offsetHeight : 0;
      
      // Return total offset with spacing
      return headerOffset + horizontalTabsHeight + 20;
    }
  
    switchTab(tabId) {
      // Close any open FAQ in current tab
      const currentTab = document.getElementById(this.activeTab);
      if (currentTab) {
        this.closeAllFAQs(currentTab);
      }
  
      // Toggle tabs (handle both button active state and parent swiper-nav-item)
      this.tabButtons.forEach(btn => {
        const isActive = btn.dataset.tab === tabId;
        
        // Toggle active class on button
        btn.classList.toggle('active', isActive);
        
        // Also toggle active class on parent swiper-tabs-nav-item if it exists (for mobile tabs)
        const parentNavItem = btn.closest('.swiper-tabs-nav-item');
        if (parentNavItem) {
          if (isActive) {
            parentNavItem.classList.add('active');
          } else {
            parentNavItem.classList.remove('active');
          }
        }
      });
  
      // Toggle content
      this.tabContents.forEach(content => {
        content.classList.toggle('hidden', content.id !== tabId);
      });
  
      this.activeTab = tabId;
      
      // Scroll to the content with offset for sticky headers
      const targetContent = document.getElementById(tabId);
      if (targetContent) {
        // Calculate dynamic offset
        const totalOffset = this.calculateDynamicOffset();
        
        // Get the position of the target content
        const targetPosition = targetContent.getBoundingClientRect().top + window.pageYOffset - totalOffset;
        
        // Smooth scroll to the position
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }
  
    closeAllFAQs(container) {
      const faqItems = container.querySelectorAll('[data-faq-item]');
      faqItems.forEach(item => {
        item.classList.remove('active');
        const button = item.querySelector('button');
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
      });
    }
  
    toggleFAQ(item) {
      // Close other FAQs in the current tab
      const currentTab = document.getElementById(this.activeTab);
      if (currentTab) {
        const otherItems = currentTab.querySelectorAll('[data-faq-item]');
        otherItems.forEach(otherItem => {
          if (otherItem !== item) {
            otherItem.classList.remove('active');
            const otherButton = otherItem.querySelector('button');
            if (otherButton) {
              otherButton.setAttribute('aria-expanded', 'false');
            }
          }
        });
      }
  
      // Toggle current FAQ
      const isActive = item.classList.contains('active');
      const button = item.querySelector('button');
      item.classList.toggle('active', !isActive);
      if (button) {
        button.setAttribute('aria-expanded', (!isActive).toString());
      }
    }
  
    destroy() {
      // Clean up all event listeners
      this.cleanup();
      
      // Clear sticky timeout
      if (this.stickyTimeout) {
        clearTimeout(this.stickyTimeout);
      }
      
      // Remove scroll listener
      if (this.faqScrollHandler) {
        window.removeEventListener('scroll', this.faqScrollHandler);
      }
      
      // Remove resize listener
      if (this.faqResizeHandler) {
        window.removeEventListener('resize', this.faqResizeHandler);
      }
      
      // Remove fixed styles from FAQ tabs nav if it exists
      if (this.faqTabsNav) {
        const navWrapper = this.faqTabsNav.closest('nav');
        if (navWrapper) {
          navWrapper.style.position = '';
          navWrapper.style.top = '';
          navWrapper.style.left = '';
          navWrapper.style.right = '';
          navWrapper.style.zIndex = '';
          navWrapper.style.backgroundColor = '';
          navWrapper.style.paddingTop = '';
          navWrapper.style.paddingBottom = '';
          navWrapper.style.transition = '';
        }
      }
      
      // Remove placeholder element
      if (this.faqNavPlaceholder && this.faqNavPlaceholder.parentNode) {
        this.faqNavPlaceholder.parentNode.removeChild(this.faqNavPlaceholder);
      }
      
      // Clear references
      this.container = null;
      this.tabButtons = null;
      this.tabContents = null;
      this.faqItems = null;
      this.faqTabsNav = null;
      this.stickyTimeout = null;
      this.faqScrollHandler = null;
      this.faqResizeHandler = null;
      this.faqNavPlaceholder = null;
    }
  }
  
  /**
   * Sticky Tabs Manager
   * Handles sticky positioning for both horizontal and vertical tabs
   */
  class StickyTabsManager {
    constructor() {
      this.horizontalTabs = null;
      this.verticalTabs = [];
      this.stickyOffset = 0;
      this.isSticky = false;
      this.placeholderElement = null;
      this.headerHeight = 0;
      this.sectionBottomBuffer = 50; // Buffer zone for smooth transitions
      this.ticking = false; // For scroll throttling
      this.resizeHandler = null; // Store resize handler for cleanup
      this.loadHandler = null; // Store load handler for cleanup
      this.headerCalcTimeout = null; // Store timeout for cleanup
      this.init();
    }
  
    calculateHeaderOffset() {
      let totalOffset = 0;
      
      // Try multiple selectors to find the header element
      const headerSelectors = [
        '.header-wrapper.header-wrapper--overlay.header-transparent',
        '.header-transparent',
        '.header-wrapper',
        'header',
        '.header',
        '[data-header]',
        'sticky-header',
        '.shopify-section-header'
      ];
      
      let header = null;
      for (const selector of headerSelectors) {
        header = document.querySelector(selector);
        if (header) break;
      }
      
      if (header) {
        const rect = header.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(header);
        
        // Calculate total offset based on header positioning
        if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') {
          // For absolute/fixed positioned headers, use the bottom edge position
          totalOffset = rect.bottom;
        } else {
          // For relative/static positioned headers, use height + margins
          const height = rect.height || header.offsetHeight || 60;
          const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
          totalOffset = height + marginBottom;
        }
      } else {
        // Fallback to default if no header found
        totalOffset = 109;
      }
      
      return totalOffset;
    }
  
    init() {
      // Find horizontal tabs container (the navigation wrapper)
      const navElement = document.querySelector('.swiper-tabs-nav')?.closest('nav');
      if (navElement) {
        this.horizontalTabs = navElement.parentElement; // Get the parent container
      }
  
      // Find the section container (parent of horizontal tabs)
      this.sectionContainer = this.horizontalTabs?.closest('.shopify-section') || 
                             this.horizontalTabs?.closest('section') ||
                             this.horizontalTabs?.closest('[data-section-id]');
  
      // Find all vertical tab sidebars
      this.verticalTabs = Array.from(document.querySelectorAll('.data-faq-container .md\\:block.col-span-2'));
  
      // Initially calculate header height
      this.headerHeight = this.calculateHeaderOffset();
  
      // Recalculate after a short delay to ensure header is fully rendered
      this.headerCalcTimeout = setTimeout(() => {
        this.headerHeight = this.calculateHeaderOffset();
        if (this.isSticky) {
          this.updateStickyPositions();
        }
      }, 100);
  
      // Also recalculate when window loads completely
      this.loadHandler = () => {
        this.headerHeight = this.calculateHeaderOffset();
        if (this.isSticky) {
          this.updateStickyPositions();
        }
      };
      window.addEventListener('load', this.loadHandler);
  
      // Check if we're on mobile
      this.isMobile = window.innerWidth < 768;
      
      // Add resize listener to update mobile state and recalculate header
      this.resizeHandler = () => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 768;
        
        // Recalculate header offset on resize
        this.headerHeight = this.calculateHeaderOffset();
        
        // If switching between mobile and desktop, update positions
        if (wasMobile !== this.isMobile) {
          if (this.isSticky) {
            this.updateStickyPositions();
          }
        } else if (this.isSticky) {
          // Update positions if window resized but still sticky
          this.updateStickyPositions();
        }
      };
      window.addEventListener('resize', this.resizeHandler);
  
      // Enable sticky on both mobile and desktop to prevent header overlap
      if (this.horizontalTabs) {
        this.setupSticky();
      }
    }
    
    updateStickyPositions() {
      if (this.horizontalTabs && this.isSticky) {
        this.horizontalTabs.style.top = `${this.headerHeight}px`;
        this.horizontalTabs.style.backgroundColor = '#f3f3f3';
        // Apply 5px padding only on mobile, keep original padding on desktop
        if (this.isMobile) {
          this.horizontalTabs.style.paddingTop = '5px';
          this.horizontalTabs.style.paddingBottom = '5px';
        } else {
          this.horizontalTabs.style.paddingTop = '15px';
          this.horizontalTabs.style.paddingBottom = '7.5px';
        }
        this.updateVerticalTabsPosition();
      }
    }
  
    setupSticky() {
      // Create a placeholder element to maintain layout when tabs go sticky
      this.placeholderElement = document.createElement('div');
      this.placeholderElement.style.display = 'none';
      this.placeholderElement.style.height = '0';
      
      // Insert placeholder before the tabs container
      if (this.horizontalTabs && this.horizontalTabs.parentNode) {
        this.horizontalTabs.parentNode.insertBefore(this.placeholderElement, this.horizontalTabs);
      }
  
      // Set initial styles for horizontal tabs
      if (this.horizontalTabs) {
        this.horizontalTabs.style.zIndex = '40';
        this.horizontalTabs.style.transition = 'all 0.3s ease';
      }
  
      // Set initial styles for vertical tabs
      this.verticalTabs.forEach(tab => {
        if (tab) {
          tab.style.transition = 'all 0.3s ease';
        }
      });
  
      // Add scroll listener with requestAnimationFrame for smooth performance
      this.handleScroll = this.handleScroll.bind(this);
      this.requestTick = this.requestTick.bind(this);
      window.addEventListener('scroll', this.requestTick, { passive: true });
      
      // Initial check
      this.handleScroll();
    }
  
    requestTick() {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }
  
    handleScroll() {
      // Safety checks
      if (!this.horizontalTabs || !this.placeholderElement) return;
  
      const rect = this.placeholderElement.getBoundingClientRect();
      const originalRect = this.horizontalTabs.getBoundingClientRect();
      
      // Check if we're still within the section bounds
      let isWithinSection = true;
      if (this.sectionContainer) {
        const sectionRect = this.sectionContainer.getBoundingClientRect();
        const sectionBottom = sectionRect.bottom;
        const horizontalTabsHeight = this.horizontalTabs.offsetHeight || 0;
        
        // Buffer zone with strong hysteresis to prevent any flickering while keeping smooth transitions
        // When sticky: unstick at 50px from bottom
        // When not sticky: only become sticky if there's 150px clearance (100px extra hysteresis)
        const effectiveBuffer = this.isSticky ? this.sectionBottomBuffer : this.sectionBottomBuffer + 100;
        
        // If the section bottom is above the horizontal tabs bottom position + buffer, we've scrolled past
        isWithinSection = sectionBottom > (this.headerHeight + horizontalTabsHeight + effectiveBuffer);
      }
      
      // Determine if we should be sticky
      const topToCheck = this.isSticky ? rect.top : originalRect.top;
      const shouldBeSticky = topToCheck <= this.headerHeight && isWithinSection;
  
      // State changes will be smooth thanks to CSS transitions
      if (shouldBeSticky && !this.isSticky) {
        this.makeSticky();
      } else if ((!shouldBeSticky || !isWithinSection) && this.isSticky) {
        this.removeSticky();
      }
  
      // Update vertical tabs sticky position
      this.updateVerticalTabsPosition();
    }
  
    makeSticky() {
      if (!this.horizontalTabs || !this.placeholderElement) return;
      
      this.isSticky = true;
      
      // Set placeholder height to maintain layout
      const tabsHeight = this.horizontalTabs.offsetHeight;
      this.placeholderElement.style.display = 'block';
      this.placeholderElement.style.height = `${tabsHeight}px`;
      
      // Make horizontal tabs sticky
      this.horizontalTabs.style.position = 'fixed';
      this.horizontalTabs.style.top = `${this.headerHeight}px`;
      this.horizontalTabs.style.left = '0';
      this.horizontalTabs.style.right = '0';
      this.horizontalTabs.style.zIndex = '40';
      this.horizontalTabs.style.backgroundColor = '#f3f3f3';
      // Apply 5px padding only on mobile, keep original padding on desktop
      if (this.isMobile) {
        this.horizontalTabs.style.paddingTop = '5px';
        this.horizontalTabs.style.paddingBottom = '5px';
      } else {
        this.horizontalTabs.style.paddingTop = '15px';
        this.horizontalTabs.style.paddingBottom = '7.5px';
      }
  
      // Update vertical tabs to be sticky (only on desktop)
      if (!this.isMobile) {
        const horizontalTabsHeight = this.horizontalTabs.offsetHeight;
        const verticalTabTop = this.headerHeight + horizontalTabsHeight + 20;
        
        this.verticalTabs.forEach(tab => {
          const parentContainer = tab.closest('.data-faq-container');
          if (tab && parentContainer && !parentContainer.classList.contains('hidden')) {
            tab.style.position = 'sticky';
            tab.style.top = `${verticalTabTop}px`;
            tab.style.alignSelf = 'flex-start';
          }
        });
      }
    }
  
    removeSticky() {
      if (!this.horizontalTabs || !this.placeholderElement) return;
      
      this.isSticky = false;
      
      // Hide placeholder
      this.placeholderElement.style.display = 'none';
      this.placeholderElement.style.height = '0';
      
      // Remove sticky from horizontal tabs
      this.horizontalTabs.style.position = '';
      this.horizontalTabs.style.top = '';
      this.horizontalTabs.style.left = '';
      this.horizontalTabs.style.right = '';
      this.horizontalTabs.style.zIndex = '';
      this.horizontalTabs.style.backgroundColor = '';
      this.horizontalTabs.style.paddingTop = '';
      this.horizontalTabs.style.paddingBottom = '';
      
      // Remove sticky from vertical tabs
      this.verticalTabs.forEach(tab => {
        if (tab) {
          tab.style.position = '';
          tab.style.top = '';
          tab.style.alignSelf = '';
        }
      });
    }
  
    updateVerticalTabsPosition() {
      // Update vertical tabs when switching between collection tabs (only on desktop)
      if (!this.isSticky || this.isMobile) return;
      
      const horizontalTabsHeight = this.horizontalTabs ? this.horizontalTabs.offsetHeight : 0;
      this.verticalTabs.forEach(tab => {
        const parentContainer = tab.closest('.data-faq-container');
        if (tab && parentContainer && !parentContainer.classList.contains('hidden')) {
          tab.style.position = 'sticky';
          tab.style.top = `${this.headerHeight + horizontalTabsHeight + 20}px`;
          tab.style.alignSelf = 'flex-start';
        }
      });
    }
  
    refreshVerticalTabs() {
      // Refresh the list of vertical tabs (useful when switching collections)
      this.verticalTabs = Array.from(document.querySelectorAll('.data-faq-container .md\\:block.col-span-2'));
      if (this.isSticky) {
        this.updateVerticalTabsPosition();
      }
    }
  
    destroy() {
      // Remove scroll listener
      if (this.requestTick) {
        window.removeEventListener('scroll', this.requestTick);
      }
      
      // Remove resize listener
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      
      // Remove load listener
      if (this.loadHandler) {
        window.removeEventListener('load', this.loadHandler);
      }
      
      // Clear timeout
      if (this.headerCalcTimeout) {
        clearTimeout(this.headerCalcTimeout);
      }
      
      // Remove sticky state
      this.removeSticky();
      
      // Remove placeholder element
      if (this.placeholderElement && this.placeholderElement.parentNode) {
        this.placeholderElement.parentNode.removeChild(this.placeholderElement);
      }
      
      // Clear references
      this.horizontalTabs = null;
      this.verticalTabs = [];
      this.sectionContainer = null;
      this.placeholderElement = null;
    }
  }
  
  /**
   * Care Item Tabs Manager
   * Handles tab switching within individual care items
   */
  class CareItemTabsManager {
    constructor() {
      this.careTabsContainers = [];
      this.init();
    }
  
    init() {
      // Find all care tabs containers
      this.careTabsContainers = Array.from(document.querySelectorAll('.care-tabs-container'));
      
      // Initialize each container
      this.careTabsContainers.forEach(container => {
        this.initializeContainer(container);
      });
    }
  
    initializeContainer(container) {
      const tabButtons = container.querySelectorAll('.care-tab-button');
      const tabContents = container.querySelectorAll('.care-tab-content');
  
      // Set initial state: find the active button and show its content
      const activeButton = Array.from(tabButtons).find(btn => 
        btn.getAttribute('aria-selected') === 'true' || btn.classList.contains('active')
      );
      
      if (activeButton) {
        const activeTab = activeButton.getAttribute('data-tab-target');
        
        // Ensure the active tab's content is visible - use more aggressive approach
        tabContents.forEach(content => {
          const contentTab = content.getAttribute('data-tab-content');
          if (contentTab === activeTab) {
            // Remove hidden class multiple ways to ensure it works
            content.classList.remove('hidden');
            content.classList.add('active');
            content.setAttribute('aria-hidden', 'false');
            // Force display with !important to override Tailwind
            content.style.cssText = 'display: block !important;';
          } else {
            content.classList.add('hidden');
            content.classList.remove('active');
            content.setAttribute('aria-hidden', 'true');
            content.style.cssText = 'display: none !important;';
          }
        });
      } else {
        // If no active button, make the first one active
        if (tabButtons.length > 0 && tabContents.length > 0) {
          const firstButton = tabButtons[0];
          const firstTab = firstButton.getAttribute('data-tab-target');
          
          // Activate first button
          firstButton.classList.add('active', 'bg-[#181D3A]', 'text-white');
          firstButton.classList.remove('text-[#181D3A]');
          firstButton.setAttribute('aria-selected', 'true');
          
          // Show first content
          tabContents.forEach(content => {
            const contentTab = content.getAttribute('data-tab-content');
            if (contentTab === firstTab) {
              content.classList.remove('hidden');
              content.classList.add('active');
              content.setAttribute('aria-hidden', 'false');
              content.style.cssText = 'display: block !important;';
            } else {
              content.classList.add('hidden');
              content.classList.remove('active');
              content.setAttribute('aria-hidden', 'true');
              content.style.cssText = 'display: none !important;';
            }
          });
        }
      }
  
      // Add click event listeners
      tabButtons.forEach(button => {
        button.addEventListener('click', function() {
          const targetTab = this.getAttribute('data-tab-target');
  
          // Update button states
          tabButtons.forEach(btn => {
            const isActive = btn.getAttribute('data-tab-target') === targetTab;
  
            if (isActive) {
              btn.classList.add('active', 'bg-[#181D3A]', 'text-white');
              btn.classList.remove('text-[#181D3A]');
              btn.setAttribute('aria-selected', 'true');
            } else {
              btn.classList.remove('active', 'bg-[#181D3A]', 'text-white');
              btn.classList.add('text-[#181D3A]');
              btn.setAttribute('aria-selected', 'false');
            }
          });
  
          // Update content visibility
          tabContents.forEach(content => {
            const contentTab = content.getAttribute('data-tab-content');
            if (contentTab === targetTab) {
              // Show this tab content
              content.classList.remove('hidden');
              content.classList.add('active');
              content.setAttribute('aria-hidden', 'false');
              // Force display with !important to override Tailwind
              content.style.cssText = 'display: block !important;';
            } else {
              // Hide this tab content
              content.classList.add('hidden');
              content.classList.remove('active');
              content.setAttribute('aria-hidden', 'true');
              content.style.cssText = 'display: none !important;';
            }
          });
        });
      });
    }
  
    refresh() {
      // Refresh to pick up any newly added care tabs containers
      this.init();
    }
  
    destroy() {
      // Event listeners will be garbage collected
      this.careTabsContainers = [];
    }
  }
  
  class CareAndManualManager {
    constructor() {
      this.tabsContainer = document.querySelector('.swiper-tabs');
      this.faqManagers = new Map();
      this.stickyManager = null;
      this.careItemTabsManager = null;
      this.init();
    }
  
    /**
     * Initialize FAQ manager for a specific container
     */
    initializeFAQManager(container) {
      if (container) {
        const managerId = container.getAttribute('data-collection-id') || 'default';
        
        // Destroy and remove old manager if exists
        if (this.faqManagers.has(managerId)) {
          const oldManager = this.faqManagers.get(managerId);
          if (oldManager && typeof oldManager.destroy === 'function') {
            oldManager.destroy();
          }
          this.faqManagers.delete(managerId);
        }
        
        // Create new manager
        const manager = new FAQManager(container);
        this.faqManagers.set(managerId, manager);
        return manager;
      }
      return null;
    }
  
    /**
     * Initialize all visible FAQ containers
     */
    initializeAllFAQs() {
      const allContainers = document.querySelectorAll('.data-faq-container');
      allContainers.forEach(container => {
        if (!container.classList.contains('hidden')) {
          this.initializeFAQManager(container);
        }
      });
    }
  
    /**
     * Switch between FAQ containers based on collection tab selection
     */
    switchFAQContainer(tabId) {
      // Hide all FAQ containers
      const allContainers = document.querySelectorAll('.data-faq-container');
      allContainers.forEach(container => {
        container.classList.add('hidden');
      });
  
      // Show the selected FAQ container
      const targetContainer = document.getElementById(tabId);
      if (targetContainer) {
        targetContainer.classList.remove('hidden');
        
        // Use requestAnimationFrame to ensure DOM is updated before initializing
        requestAnimationFrame(() => {
          // Initialize FAQ manager for the newly visible container
          this.initializeFAQManager(targetContainer);
          
          // Refresh vertical tabs in sticky manager
          if (this.stickyManager) {
            this.stickyManager.refreshVerticalTabs();
          }
        });
      }
    }
  
    /**
     * Set active state for collection tab
     */
    setActiveTab(target) {
      if (!target) return;
      this.tabsContainer.querySelectorAll('div').forEach(div => {
        div.classList.remove('active', '[&.active]:bg-black', '[&.active]:text-[#CCCCCC]');
      });
      target.classList.add('active', '[&.active]:bg-black', '[&.active]:text-[#CCCCCC]');
    }
  
    init() {
      // Initialize sticky tabs manager
      this.stickyManager = new StickyTabsManager();
      
      // Initialize care item tabs manager
      this.careItemTabsManager = new CareItemTabsManager();
      
      if (this.tabsContainer) {
        // Handle collection tab clicks
        this.tabsContainer.addEventListener('click', (e) => {
          const link = e.target.closest('.collection-filter-link');
          if (!link) return;
          
          const collectionTab = link.getAttribute('data-collection-tab');
          if (!collectionTab) return;
          
          e.preventDefault();
          
          const parentDiv = link.closest('div');
          this.setActiveTab(parentDiv);
          
          // Switch FAQ container
          this.switchFAQContainer(collectionTab);
        });
      }
  
      // Initialize FAQs for all visible containers on page load
      this.initializeAllFAQs();
  
      // Initialize the first collection tab as active if none is active
      if (this.tabsContainer) {
        const firstTab = this.tabsContainer.querySelector('.collection-filter-link');
        if (firstTab) {
          const firstTabId = firstTab.getAttribute('data-collection-tab');
          if (firstTabId) {
            const firstContainer = document.getElementById(firstTabId);
            if (firstContainer && !firstContainer.classList.contains('hidden')) {
              this.initializeFAQManager(firstContainer);
            }
          }
        }
      }
    }
  
    destroy() {
      // Clean up sticky manager
      if (this.stickyManager) {
        this.stickyManager.destroy();
        this.stickyManager = null;
      }
      
      // Clean up care item tabs manager
      if (this.careItemTabsManager) {
        this.careItemTabsManager.destroy();
        this.careItemTabsManager = null;
      }
      
      // Clean up FAQ managers
      this.faqManagers.forEach(manager => {
        if (manager && typeof manager.destroy === 'function') {
          manager.destroy();
        }
      });
      this.faqManagers.clear();
    }
  }
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    new CareAndManualManager();
  });
  
  